import { useState, useEffect } from 'react';
import { useDisclosure, useClipboard, useDebouncedValue } from '@mantine/hooks';
import {
    Container,
    Title,
    Button,
    Table,
    Modal,
    TextInput,
    Stack,
    Badge,
    ActionIcon,
    Group,
    Alert,
    Code,
    Text,
    Skeleton,
    Tooltip,
    Select,
    Pagination,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash, IconAlertCircle, IconCopy, IconCheck, IconPower, IconSearch } from '@tabler/icons-react';
import type { AxiosError } from 'axios';
import { apiKeysApi } from '../api/client';

interface ApiKey {
    id: number;
    user_id?: number;
    owner_username?: string | null;
    description: string;
    is_active: boolean;
    created_at: string;
    last_used: string | null;
}

export default function ApiKeys({ isAdmin = false }: { isAdmin?: boolean }) {
    const [keys, setKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 25;
    const [modalOpened, setModalOpened] = useState(false);
    const [createdKey, setCreatedKey] = useState<string | null>(null);
    const [revokeTarget, setRevokeTarget] = useState<number | null>(null);
    const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
    const clipboard = useClipboard({ timeout: 2000 });

    const form = useForm({
        initialValues: { description: '' },
        validate: {
            description: (v) => (v.trim() ? null : 'Description is required'),
        },
    });

    const loadKeys = async () => {
        setLoading(true);
        try {
            const result = await apiKeysApi.list({ search: debouncedSearch, status: statusFilter, page, pageSize });
            setKeys(Array.isArray(result.data) ? (result.data as unknown as ApiKey[]) : []);
            setTotal(result.total);
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: 'Failed to load API keys',
                color: 'red',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadKeys();
    }, [debouncedSearch, statusFilter, page]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, statusFilter]);

    const handleCreate = async (values: typeof form.values) => {
        try {
            const result = await apiKeysApi.create(values.description.trim());
            setCreatedKey(result.key);
            form.reset();
            setModalOpened(false);
            loadKeys();
        } catch (err) {
            const error = err as AxiosError<{ error: string }>;
            notifications.show({
                title: 'Error',
                message: error.response?.data?.error || 'Failed to create API key',
                color: 'red',
            });
        }
    };

    const handleRevoke = (id: number) => {
        setRevokeTarget(id);
        openConfirm();
    };

    const handleToggle = async (id: number) => {
        try {
            await apiKeysApi.toggle(id);
            loadKeys();
        } catch (error) {
            notifications.show({ title: 'Error', message: 'Failed to toggle API key', color: 'red' });
        }
    };

    const confirmRevoke = async () => {
        if (revokeTarget === null) return;
        closeConfirm();
        try {
            await apiKeysApi.revoke(revokeTarget);
            notifications.show({
                title: 'Success',
                message: 'API key revoked',
                color: 'green',
            });
            loadKeys();
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: 'Failed to revoke API key',
                color: 'red',
            });
        } finally {
            setRevokeTarget(null);
        }
    };

    const rows = keys.map((k) => (
        <Table.Tr key={k.id}>
            <Table.Td>{k.description}</Table.Td>
            {isAdmin && (
                <Table.Td>
                    <Text size="sm">{k.owner_username ?? '—'}</Text>
                    {k.user_id != null && <Text size="xs" c="dimmed"># {k.user_id}</Text>}
                </Table.Td>
            )}
            <Table.Td>
                <Badge color={k.is_active ? 'green' : 'gray'}>
                    {k.is_active ? 'Active' : 'Inactive'}
                </Badge>
            </Table.Td>
            <Table.Td>{new Date(k.created_at).toLocaleString()}</Table.Td>
            <Table.Td>
                {k.last_used ? new Date(k.last_used).toLocaleString() : '—'}
            </Table.Td>
            <Table.Td>
                <Group gap="xs">
                    <Tooltip label={k.is_active ? 'Disable API key' : 'Enable API key'}>
                        <ActionIcon
                            color={k.is_active ? 'orange' : 'green'}
                            variant="light"
                            onClick={() => handleToggle(k.id)}
                            aria-label={k.is_active ? 'Disable API key' : 'Enable API key'}
                        >
                            <IconPower size={16} />
                        </ActionIcon>
                    </Tooltip>
                    {isAdmin && (
                        <Tooltip label="Delete API key">
                            <ActionIcon
                                color="red"
                                variant="light"
                                onClick={() => handleRevoke(k.id)}
                                aria-label="Delete API key"
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>
            </Table.Td>
        </Table.Tr>
    ));

    return (
        <Container size="xl">
            <Group justify="space-between" mb="xl">
                <Title order={1}>API Keys</Title>
                <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => {
                        setCreatedKey(null);
                        form.reset();
                        setModalOpened(true);
                    }}
                >
                    New Key
                </Button>
            </Group>

            <Group mb="md">
                <TextInput
                    placeholder="Search by description or owner..."
                    leftSection={<IconSearch size={16} />}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    style={{ flex: 1 }}
                />
                <Select
                    data={[
                        { value: 'all', label: 'All statuses' },
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' },
                    ]}
                    value={statusFilter}
                    onChange={(value) => setStatusFilter((value as 'all' | 'active' | 'inactive') ?? 'all')}
                    allowDeselect={false}
                    w={160}
                />
            </Group>

            <Group justify="space-between" mb="md">
                <Text size="sm" c="dimmed">
                    {keys.length} of {total} result{total !== 1 ? 's' : ''}
                </Text>
                <Pagination value={page} onChange={setPage} total={Math.max(1, Math.ceil(total / pageSize))} size="sm" />
            </Group>

            {createdKey && (
                <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="yellow"
                    mb="lg"
                    title="Save this key — it will not be shown again"
                    withCloseButton
                    onClose={() => setCreatedKey(null)}
                >
                    <Code block mb="xs">
                        {createdKey}
                    </Code>
                    <Group justify="space-between" align="center" mt="xs">
                        <Text size="sm">
                            Add it to your MCP client config as the{' '}
                            <Code>X-API-Key</Code> header.
                        </Text>
                        <Button
                            size="xs"
                            variant="outline"
                            color={clipboard.copied ? 'green' : 'yellow'}
                            leftSection={clipboard.copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                            onClick={() => createdKey && clipboard.copy(createdKey)}
                        >
                            {clipboard.copied ? 'Copied!' : 'Copy key'}
                        </Button>
                    </Group>
                </Alert>
            )}

            {loading ? (
                <Stack gap="xs">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} height={44} />
                    ))}
                </Stack>
            ) : (
                <Table striped highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Description</Table.Th>
                            {isAdmin && <Table.Th>Owner</Table.Th>}
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Created</Table.Th>
                            <Table.Th>Last used</Table.Th>
                            <Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rows.length > 0 ? (
                            rows
                        ) : (
                            <Table.Tr>
                                <Table.Td colSpan={isAdmin ? 6 : 5}>
                                    <Text c="dimmed" ta="center" py="md">
                                        {search || statusFilter !== 'all'
                                            ? 'No API keys match your search or filters.'
                                            : 'No API keys yet. Create one to connect MCP clients.'}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                    </Table.Tbody>
                </Table>
            )}

            <Group justify="space-between" mt="md">
                <Text size="sm" c="dimmed">
                    {keys.length} of {total} result{total !== 1 ? 's' : ''}
                </Text>
                <Pagination value={page} onChange={setPage} total={Math.max(1, Math.ceil(total / pageSize))} />
            </Group>

            <Modal
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                title="Create API Key"
            >
                <form onSubmit={form.onSubmit(handleCreate)}>
                    <Stack>
                        <TextInput
                            label="Description"
                            placeholder="e.g. VS Code Copilot, Claude Desktop"
                            {...form.getInputProps('description')}
                        />
                        <Button type="submit">Create</Button>
                    </Stack>
                </form>
            </Modal>

            <Modal
                opened={confirmOpened}
                onClose={closeConfirm}
                title="Revoke API key"
                size="sm"
            >
                <Stack>
                    <Text>Revoke this API key? MCP clients using it will immediately lose access.</Text>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={closeConfirm}>Cancel</Button>
                        <Button color="red" onClick={confirmRevoke}>Revoke</Button>
                    </Group>
                </Stack>
            </Modal>
        </Container>
    );
}
